import { useEffect, useCallback, useRef, useState } from "react";
import Swal from "sweetalert2";
import {
  validateGuardSupervisorAccess,
  type AccessValidationResult,
} from "../utils/accessValidation";
import { useUserStore } from "../store/userStore";
import {
  getUserAssignedYardIds,
  TRUCK_YARD_GUARD_ROLE_ID,
  TRUCK_YARD_INSPECTOR_ROLE_ID,
  TRUCK_YARD_SUPERVISOR_ROLE_ID,
} from "./useTruckYardRole";
import { logoutUser } from "../pages/login/functions";

// Validation interval in milliseconds (5 minutes)
const VALIDATION_INTERVAL = 5 * 60 * 1000;

/**
 * Lee el toggle de validación por geocerca que el login dejó en localStorage.
 * Solo "false" apaga la geocerca: si la llave no existe (sesiones abiertas antes
 * del toggle, o usuarios sin el campo) se valida como siempre.
 */
function getStoredGeofenceValidation(): boolean {
  return localStorage.getItem("userGeofenceValidation") !== "false";
}

interface UseAccessValidationOptions {
  enabled?: boolean;
  onAccessDenied?: (result: AccessValidationResult) => void;
}

/**
 * Hook que valida continuamente si un guardia o supervisor
 * sigue dentro del geocerca y su horario laboral.
 * Si la validación falla, muestra un modal y redirige al login.
 */
export function useAccessValidation(options: UseAccessValidationOptions = {}) {
  const { enabled = true, onAccessDenied } = options;
  const user = useUserStore((state) => state.user);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [lastValidation, setLastValidation] = useState<Date | null>(null);
  const isShowingModal = useRef(false);

  const userRoleId = user?.role_id || localStorage.getItem("userRoleId") || "";

  // Check if user requires access validation
  const requiresValidation =
    userRoleId === TRUCK_YARD_GUARD_ROLE_ID ||
    userRoleId === TRUCK_YARD_SUPERVISOR_ROLE_ID ||
    userRoleId === TRUCK_YARD_INSPECTOR_ROLE_ID;

  const showAccessDeniedModal = useCallback(
    async (result: AccessValidationResult) => {
      if (isShowingModal.current) return;
      isShowingModal.current = true;

      let title = "Acceso Denegado";
      let text = "";

      switch (result.reason) {
        case "outside_geofence":
          title = "Fuera del Área de Trabajo";
          text = `Ya no te encuentras dentro del perímetro de ${result.yardName || "el patio de maniobras"}. Tu sesión será cerrada.`;
          break;
        case "outside_schedule":
          title = "Fuera de Horario Laboral";
          text = `Tu horario de trabajo ha finalizado. Horario: ${result.scheduleInfo?.from || ""} - ${result.scheduleInfo?.to || ""}. Hora actual: ${result.scheduleInfo?.currentTime || ""}. Tu sesión será cerrada.`;
          break;
        case "no_yard_assigned":
          title = "Sin Patio Asignado";
          text =
            "No tienes un patio de maniobras asignado. Contacta a tu administrador.";
          break;
        case "geolocation_error":
          title = "Error de Ubicación";
          text =
            "No se pudo verificar tu ubicación. Por favor, habilita los permisos de ubicación.";
          break;
      }

      // Call custom handler if provided
      if (onAccessDenied) {
        onAccessDenied(result);
      }

      await Swal.fire({
        title,
        text,
        icon: "warning",
        confirmButtonText: "Ir al Login",
        confirmButtonColor: "#6b7280",
        allowOutsideClick: false,
        allowEscapeKey: false,
        showCancelButton: false,
      });

      // Clear interval before logout
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }

      isShowingModal.current = false;

      // Logout and redirect
      await logoutUser();
    },
    [onAccessDenied],
  );

  const validateAccess = useCallback(async () => {
    if (!requiresValidation || !enabled || isShowingModal.current) return;

    const token = localStorage.getItem("token");
    if (!token) return;

    setIsValidating(true);

    try {
      // Get user data from store or localStorage
      const userData = {
        // Un solo campo (issue #497). getUserAssignedYardIds ya cubre la sesión
        // vieja que sólo tiene el singular en localStorage.
        yard_ids:
          user?.yard_ids && user.yard_ids.length > 0
            ? user.yard_ids
            : getUserAssignedYardIds(),
        schedule: user?.schedule,
        geofence_validation_enabled:
          user?.geofence_validation_enabled ?? getStoredGeofenceValidation(),
      };

      const result = await validateGuardSupervisorAccess(userData, token);

      setLastValidation(new Date());

      if (!result.allowed) {
        await showAccessDeniedModal(result);
      }
    } catch (error) {
      console.error("Error validating access:", error);
    } finally {
      setIsValidating(false);
    }
  }, [requiresValidation, enabled, user, showAccessDeniedModal]);

  // Set up periodic validation
  useEffect(() => {
    if (!requiresValidation || !enabled) return;

    // Initial validation after a short delay (to allow page to load)
    const initialTimeout = setTimeout(() => {
      console.log("[useAccessValidation] Running initial validation...");
      validateAccess();
    }, 1000);

    // Set up periodic validation
    intervalRef.current = setInterval(() => {
      console.log("[useAccessValidation] Running periodic validation...");
      validateAccess();
    }, VALIDATION_INTERVAL);

    return () => {
      clearTimeout(initialTimeout);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [requiresValidation, enabled, validateAccess]);

  // Validate on visibility change (when user returns to tab)
  useEffect(() => {
    if (!requiresValidation || !enabled) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        validateAccess();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [requiresValidation, enabled, validateAccess]);

  return {
    isValidating,
    lastValidation,
    validateNow: validateAccess,
    requiresValidation,
  };
}

/**
 * Wrapper para validar acceso antes de una acción/petición
 * Útil para validar antes de operaciones críticas
 */
export async function validateBeforeAction(): Promise<boolean> {
  const token = localStorage.getItem("token");
  const userRoleId = localStorage.getItem("userRoleId") || "";

  const requiresValidation =
    userRoleId === TRUCK_YARD_GUARD_ROLE_ID ||
    userRoleId === TRUCK_YARD_SUPERVISOR_ROLE_ID ||
    userRoleId === TRUCK_YARD_INSPECTOR_ROLE_ID;

  if (!requiresValidation || !token) {
    return true; // No validation needed
  }

  const userData = {
    yard_ids: getUserAssignedYardIds(),
    schedule: undefined, // Will be fetched from user data if needed
    geofence_validation_enabled: getStoredGeofenceValidation(),
  };

  const result = await validateGuardSupervisorAccess(userData, token);
  return result.allowed;
}

export default useAccessValidation;
